import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InitiativeWriteInput } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WizardStepper } from './WizardStepper';
import { WIZARD_STEPS, stepIndex, type WizardStepId } from './wizard-steps';
import {
  isSectionVisible,
  visibilityFromForm,
  affectedFieldPathsOnToggleOff,
  pruneForSubmit,
  type ConditionalSection,
} from './intake-form-model';
import { emptyIntakeValues, type IntakeOptionSets } from './intake-options';
import {
  missingCountByStep,
  missingRequiredFields,
  type MissingRequiredField,
} from './required-fields';
import { YesNoToggle, Field } from './fields';
import { BasicsStep, TimingStep, ClassificationStep, AudienceStep, GovernanceStep, ValidationStep } from './steps';
import { MilestonesStep, DependenciesStep } from './steps-collections';
import { FieldForceStep } from './FieldForceStep';
import { DiscardConfirmation, UnsavedNavigationPrompt, SessionEndedNotice } from './IntakeDialogs';
import { ConflictDialog } from './ConflictDialog';
import { useValidateInitiative } from './use-validate-initiative';
import { evaluateClientHardRules, mergeFindings, type SummaryFinding } from './validation-model';
import type { VersionConflict } from './intake-data';

type FormValues = InitiativeWriteInput;

/** A pending discard confirmation: the section being turned off and its affected fields. */
interface PendingDiscard {
  readonly section: ConditionalSection;
  readonly affectedFieldPaths: readonly string[];
}

/** Autosave status shown right-aligned in the footer (UI spec §6). */
type AutosaveState = 'idle' | 'saving' | 'saved';

export interface IntakeWizardProps {
  readonly options: IntakeOptionSets;
  /** Initial values when editing an existing record; a blank payload for a new one. */
  readonly defaultValues?: InitiativeWriteInput;
  /** Called with the pruned payload on submit (R5.10 prune applied here). */
  readonly onSubmit: (payload: InitiativeWriteInput) => void;
  /**
   * Save as Draft — permitted despite governance findings (R8.4). Defaults to
   * {@link onSubmit} when the caller does not distinguish the two.
   */
  readonly onSaveDraft?: (payload: InitiativeWriteInput) => void;
  /** Attempt to leave the form; returns whether navigation may proceed immediately. */
  readonly onRequestLeave?: () => void;
  /** True when a prior session ended with unsaved input (R5.12). */
  readonly sessionEndedWithUnsavedInput?: boolean;
  /** The record id when editing; drives the record-scoped validate/update paths. */
  readonly initiativeId?: string;
  /** The loaded record version when editing; the validate call carries it. */
  readonly loadedVersion?: number | null;
  /**
   * A version conflict returned by the last submit (R5.8). When present the
   * conflict dialog is shown; the form values are kept untouched so the user can
   * resubmit against the new version. Dismiss via {@link onDismissConflict}.
   */
  readonly versionConflict?: VersionConflict | null;
  /**
   * Findings the server returned when a *save* was rejected (R8.2, R8.12), as
   * opposed to the live re-validation's.
   *
   * Merged into the validation summary rather than shown separately, because to the
   * user they are the same thing: reasons this record cannot be saved. The
   * distinction that matters is only that these arrived from the attempt, so they
   * survive until the next attempt or the next committed field change instead of
   * being replaced 300 ms later by a re-validation that never saw the rejection.
   */
  readonly submitFindings?: readonly SummaryFinding[];
  /** Dismiss the version-conflict dialog; the form values stay as entered (R5.8). */
  readonly onDismissConflict?: () => void;
}

/**
 * The Initiative_Intake_Form wizard (task 23.1): nine steps, per-field RHF
 * subscriptions, conditional visibility driven by the domain
 * {@link visibilityFromForm} map, and the discard / unsaved-navigation / session-
 * ended flows of R5.10-R5.12.
 *
 * Involvement is a first-class control here rather than inside a step, because a
 * yes→no toggle is what triggers the discard confirmation (R5.10) and because the
 * visibility of the Field Force and Dependencies steps' conditional blocks is
 * read from the same domain map the server validates against — so what the wizard
 * renders and what the server checks are the same set (Property 23).
 */
export function IntakeWizard({
  options,
  defaultValues,
  onSubmit,
  onSaveDraft,
  submitFindings = [],
  onRequestLeave,
  sessionEndedWithUnsavedInput = false,
  initiativeId,
  loadedVersion,
  versionConflict = null,
  onDismissConflict,
}: IntakeWizardProps): React.JSX.Element {
  const { t } = useTranslation();
  const form = useForm<FormValues>({ defaultValues: defaultValues ?? emptyIntakeValues(), mode: 'onBlur' });
  const [activeStep, setActiveStep] = useState<WizardStepId>('basics');
  const [completed, setCompleted] = useState<ReadonlySet<WizardStepId>>(new Set());
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null);
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const [showSessionEnded, setShowSessionEnded] = useState(sessionEndedWithUnsavedInput);
  const [autosave] = useState<AutosaveState>('idle');
  // Warnings the user has acknowledged this editing session (R8.5). A warning that
  // is no longer raised by the current evaluation simply never appears, so a stale
  // acknowledgement is harmless; a re-raised warning re-enters the unacknowledged
  // set until acknowledged again.
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<ReadonlySet<string>>(new Set());

  const {
    findings: serverFindings,
    unacknowledgedWarningRuleIds: serverUnackWarnings,
    serverEvaluated,
    isRevalidating,
    revalidate,
  } = useValidateInitiative(initiativeId, loadedVersion);

  const activeIndex = stepIndex(activeStep);

  // The live form values; drives both the client-side syntactic pre-check and the
  // debounced server re-validation (R5.7). useWatch re-runs this on every commit.
  const liveValues = useWatch({ control: form.control }) as Partial<FormValues>;
  // A stable serialisation of the watched values: it changes only on a genuine
  // value change, not on every render (useWatch returns a fresh object each time),
  // so it is the honest dependency for the effect and the memo below.
  const liveValuesKey = JSON.stringify(liveValues);

  // Re-run the authoritative server validation on committed field changes,
  // debounced at 300 ms by the hook (R5.7). Reading the values from the form
  // inside the effect (rather than closing over `liveValues`) keeps the
  // dependency list exactly the change key and the stable callbacks.
  useEffect(() => {
    revalidate(form.getValues() as Partial<FormValues>);
  }, [liveValuesKey, revalidate, form]);

  // Merge the authoritative server findings with the client syntactic hard rules
  // (R5.7, R8.12): the server set stands alone once it has evaluated; before that
  // the client findings stand alone so the summary is never blank while debouncing.
  const clientFindings = useMemo(
    () => evaluateClientHardRules(form.getValues() as Partial<FormValues>),
    [liveValuesKey, form],
  );

  // Required inputs still empty anywhere in the intake, with the step that owns
  // each (Step 9's advisory). Computed here rather than in the step body because
  // it spans every step, and locally rather than from the findings because the
  // server verdict is debounced and may never arrive — see `required-fields.ts`.
  // Conditional sections are honoured through the same visibility map the body
  // renders from, so a hidden section is never reported as incomplete (R5.3).
  const missingFields = useMemo(() => {
    const values = form.getValues() as Partial<FormValues>;
    const visibility = visibilityFromForm({
      fieldForceInvolvement: Boolean(values.fieldForceInvolvement),
      dteInvolvement: Boolean(values.dteInvolvement),
      cscInvolvement: Boolean(values.cscInvolvement),
    });
    return missingRequiredFields(values, visibility);
  }, [liveValuesKey, form]);

  /** The same set folded to a per-step count, for the stepper's markers. */
  const missingStepCounts = useMemo(() => missingCountByStep(missingFields), [missingFields]);
  const findings = useMemo(() => {
    const live = mergeFindings(serverFindings, clientFindings, serverEvaluated);
    if (submitFindings.length === 0) {
      return live;
    }
    // A rejection's findings first, then any live finding it did not already
    // report: the same problem listed twice reads as two problems.
    //
    // Keyed on `messageKey`, not `ruleId`. The client pre-check and the server
    // registry name the same rule differently by design - `owner.missing` here
    // versus `HV-002` there - so identifiers never match across the two sources.
    // The message key does: it is the sentence the user reads, and
    // `assertValidationRegistryConsistent` keeps it unique per rule, so two
    // findings sharing one are the same unmet rule seen at two moments.
    const reported = new Set(submitFindings.map((finding) => finding.messageKey));
    return [...submitFindings, ...live.filter((finding) => !reported.has(finding.messageKey))];
  }, [serverFindings, clientFindings, serverEvaluated, submitFindings]);

  // A warning is unacknowledged when the current evaluation still raises it and the
  // user has not acknowledged it this session (R8.5). Client-only evaluation raises
  // no warnings, so this tracks the server set filtered by acknowledgements.
  const unacknowledgedWarningRuleIds = useMemo(
    () => serverUnackWarnings.filter((ruleId) => !acknowledgedWarnings.has(ruleId)),
    [serverUnackWarnings, acknowledgedWarnings],
  );

  const acknowledgeWarning = useCallback((ruleId: string): void => {
    setAcknowledgedWarnings((current) => new Set(current).add(ruleId));
  }, []);

  const goToStep = useCallback((step: WizardStepId): void => {
    setActiveStep(step);
  }, []);

  const markCompleteAndAdvance = useCallback((): void => {
    setCompleted((current) => new Set(current).add(activeStep));
    const next = WIZARD_STEPS[activeIndex + 1];
    if (next) setActiveStep(next.id);
  }, [activeIndex, activeStep]);

  const goBack = useCallback((): void => {
    const previous = WIZARD_STEPS[activeIndex - 1];
    if (previous) setActiveStep(previous.id);
  }, [activeIndex]);

  const submit = form.handleSubmit((values) => {
    onSubmit(pruneForSubmit(values));
  });

  // Save as Draft (R8.4): permitted even with governance findings. Falls back to
  // the plain submit when the caller does not distinguish the two paths.
  const saveDraft = form.handleSubmit((values) => {
    (onSaveDraft ?? onSubmit)(pruneForSubmit(values));
  });

  const requestLeave = useCallback((): void => {
    if (form.formState.isDirty) {
      setShowLeavePrompt(true);
    } else {
      onRequestLeave?.();
    }
  }, [form.formState.isDirty, onRequestLeave]);

  return (
    <FormProvider {...form}>
      {showSessionEnded ? <SessionEndedNotice onDismiss={() => setShowSessionEnded(false)} /> : null}

      {/* The stepper marks which steps still owe a required input, so the tick can
          no longer sit beside an incomplete step and the user can see where the
          gaps are without opening each one. */}
      <WizardStepper
        activeStep={activeStep}
        completedSteps={completed}
        missingCountByStep={missingStepCounts}
        onSelect={goToStep}
      />

      <Card className="mt-3 gap-0 p-0">
        <div className="p-[16px]">
          <InvolvementControls form={form} onToggleOffWithValues={setPendingDiscard} />
          <StepBody
            activeStep={activeStep}
            options={options}
            form={form}
            validation={{
              findings,
              unacknowledgedWarningRuleIds,
              onAcknowledgeWarning: acknowledgeWarning,
              onSaveDraft: () => void saveDraft(),
              onSubmit: () => void submit(),
              isRevalidating,
              missingFields,
              onNavigateToStep: goToStep,
            }}
          />
        </div>

        {/* Footer: ← Back · primary Continue → · skip to validation · right-aligned
            autosave status. */}
        <div className="border-line flex items-center justify-between gap-3 border-t bg-[var(--surface-alt)] px-[16px] py-3">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={goBack} disabled={activeIndex <= 0}>
              {t('intake.nav.back')}
            </Button>
            {activeIndex < WIZARD_STEPS.length - 1 ? (
              <Button size="sm" onClick={markCompleteAndAdvance}>
                {t('intake.nav.continue')}
              </Button>
            ) : null}
            {/* Jump straight to Validation, which owns Save draft / Submit for
                review (UI spec §6.3). The stepper already allows the jump, but it
                is a small tab in the top bar; editing an existing Initiative is
                usually "change one field, then save", so the shortcut belongs
                beside the action the user is already looking at. It carries the
                missing-required-input count, so the user can tell from any step
                whether a jump lands on a summary that is ready to submit.
                Hidden on the final step, where it would point at itself. */}
            {activeStep !== 'validation' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToStep('validation')}
                title={t('intake.nav.skipToValidationHint')}
              >
                {missingFields.length > 0
                  ? t('intake.nav.skipToValidationWithMissing', { count: missingFields.length })
                  : t('intake.nav.skipToValidation')}
              </Button>
            ) : null}
            {/* On the final step the validation summary owns the Save draft /
                Submit for review actions and their gate (UI spec §6.3), so the
                footer shows no primary action there — only Back and Reset. */}
            <Button variant="ghost" size="sm" onClick={requestLeave}>
              {t('common.reset')}
            </Button>
          </div>
          <span className="text-muted-foreground text-[11px]" aria-live="polite">
            {autosave === 'saving'
              ? t('intake.autosave.saving')
              : autosave === 'saved'
                ? t('intake.autosave.saved')
                : t('intake.autosave.idle')}
          </span>
        </div>
      </Card>

      {pendingDiscard ? (
        <DiscardConfirmation
          section={pendingDiscard.section}
          affectedFieldPaths={pendingDiscard.affectedFieldPaths}
          onCancel={() => {
            // Cancel is a genuine no-op (R5.10): restore the flag to yes; the
            // values were never removed, so nothing to put back.
            form.setValue(involvementFieldOf(pendingDiscard.section), true, { shouldDirty: true });
            setPendingDiscard(null);
          }}
          onConfirm={() => {
            // Confirm keeps the flag at no; the values stay in state and are
            // pruned at submit by pruneForSubmit, so this stays reversible until
            // the user actually saves.
            setPendingDiscard(null);
          }}
        />
      ) : null}

      {showLeavePrompt ? (
        <UnsavedNavigationPrompt
          onContinue={() => setShowLeavePrompt(false)}
          onSave={() => {
            setShowLeavePrompt(false);
            void submit();
          }}
          onDiscard={() => {
            setShowLeavePrompt(false);
            form.reset(defaultValues ?? emptyIntakeValues());
            onRequestLeave?.();
          }}
        />
      ) : null}

      {versionConflict ? (
        <ConflictDialog
          isOpen
          fields={versionConflict.fields}
          storedVersion={versionConflict.storedVersion}
          submittedVersion={versionConflict.submittedVersion}
          onDismiss={() => onDismissConflict?.()}
        />
      ) : null}
    </FormProvider>
  );
}

/** The write-shape field name of a section's involvement flag. */
function involvementFieldOf(section: ConditionalSection): keyof FormValues {
  switch (section) {
    case 'fieldForce':
      return 'fieldForceInvolvement';
    case 'dte':
      return 'dteInvolvement';
    case 'csc':
      return 'cscInvolvement';
  }
}

/**
 * The three involvement toggles (R5.2, R5.3, R5.10). A yes→no toggle that would
 * discard held values raises the discard confirmation instead of applying
 * immediately; the flag is only left at no once the user confirms — but the
 * values stay put either way, so cancel is a no-op.
 */
function InvolvementControls({
  form,
  onToggleOffWithValues,
}: {
  readonly form: UseFormReturn<FormValues>;
  readonly onToggleOffWithValues: (pending: PendingDiscard) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const values = useWatch({ control: form.control });

  const setInvolvement = (section: ConditionalSection, next: boolean): void => {
    if (!next) {
      const affected = affectedFieldPathsOnToggleOff(section, {
        fieldForceScope: values.fieldForceScope,
        dependencies: values.dependencies,
      });
      if (affected.length > 0) {
        // Reflect the requested "no" so the section collapses, then confirm.
        form.setValue(involvementFieldOf(section), false, { shouldDirty: true });
        onToggleOffWithValues({ section, affectedFieldPaths: affected });
        return;
      }
    }
    form.setValue(involvementFieldOf(section), next, { shouldDirty: true });
  };

  return (
    <div className="mb-4 flex flex-wrap gap-6">
      <Field label={t('intake.fieldForce.involvementLabel')}>
        <YesNoToggle
          name={t('intake.fieldForce.involvementLabel')}
          value={Boolean(values.fieldForceInvolvement)}
          onChange={(next) => setInvolvement('fieldForce', next)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
      </Field>
      <Field label={t('intake.dependencies.dteInvolvementLabel')}>
        <YesNoToggle
          name={t('intake.dependencies.dteInvolvementLabel')}
          value={Boolean(values.dteInvolvement)}
          onChange={(next) => setInvolvement('dte', next)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
      </Field>
      <Field label={t('intake.dependencies.cscInvolvementLabel')}>
        <YesNoToggle
          name={t('intake.dependencies.cscInvolvementLabel')}
          value={Boolean(values.cscInvolvement)}
          onChange={(next) => setInvolvement('csc', next)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
      </Field>
    </div>
  );
}

/**
 * The active step's panel. Reads the domain visibility map from the current
 * involvement flags so the Field Force and Dependencies steps show exactly the
 * conditional blocks the server would validate.
 */
function StepBody({
  activeStep,
  options,
  form,
  validation,
}: {
  readonly activeStep: WizardStepId;
  readonly options: IntakeOptionSets;
  readonly form: UseFormReturn<FormValues>;
  readonly validation: ValidationStepProps;
}): React.JSX.Element {
  const flags = useWatch({
    control: form.control,
    name: ['fieldForceInvolvement', 'dteInvolvement', 'cscInvolvement', 'audienceIds'],
  });
  const [ffInvolvement, dteInvolvement, cscInvolvement, audienceIds] = flags;

  const visibility = useMemo(
    () =>
      visibilityFromForm({
        fieldForceInvolvement: Boolean(ffInvolvement),
        dteInvolvement: Boolean(dteInvolvement),
        cscInvolvement: Boolean(cscInvolvement),
      }),
    [ffInvolvement, dteInvolvement, cscInvolvement],
  );

  const showFieldForce = isSectionVisible('fieldForce', visibility);
  const showDte = isSectionVisible('dte', visibility);
  const showCsc = isSectionVisible('csc', visibility);

  // KAM/IND named-customer block (UI spec §6.1, R5.4): the audience option whose
  // id ends in a kam/ind code drives the reveal. See the AudienceStep note.
  const showNamedCustomerBlock = ((audienceIds as string[] | undefined) ?? []).some((id) => {
    const option = options.audiences.find((candidate) => candidate.id === id);
    const label = option?.label.toLowerCase() ?? '';
    return id.toLowerCase().includes('kam') || id.toLowerCase().includes('ind') || label.includes('kam') || label.includes('ind');
  });

  switch (activeStep) {
    case 'basics':
      return <BasicsStep options={options} />;
    case 'timing':
      return <TimingStep />;
    case 'classification':
      return <ClassificationStep options={options} />;
    case 'audience':
      return <AudienceStep options={options} showNamedCustomerBlock={showNamedCustomerBlock} />;
    case 'fieldForce':
      return showFieldForce ? <FieldForceStep options={options} /> : <InactiveSectionNote />;
    case 'milestones':
      return <MilestonesStep options={options} />;
    case 'dependencies':
      return <DependenciesStep showDte={showDte} showCsc={showCsc} options={options} />;
    case 'governance':
      return <GovernanceStep />;
    case 'validation':
      return <ValidationStep {...validation} />;
  }
}

/** The validation-step props the wizard threads into {@link ValidationStep}. */
interface ValidationStepProps {
  readonly findings: readonly SummaryFinding[];
  readonly unacknowledgedWarningRuleIds: readonly string[];
  readonly onAcknowledgeWarning: (ruleId: string) => void;
  readonly onSaveDraft: () => void;
  readonly onSubmit: () => void;
  readonly isRevalidating: boolean;
  readonly missingFields: readonly MissingRequiredField[];
  readonly onNavigateToStep: (step: WizardStepId) => void;
}

/** Shown for a conditional step whose involvement flag is currently no. */
function InactiveSectionNote(): React.JSX.Element {
  const { t } = useTranslation();
  return <p className="text-muted-foreground text-[12px]">{t('intake.fieldForce.calcPreviewNote')}</p>;
}
